import clsx from "clsx";
import React, { memo, useMemo } from "react";
import NotifyDot from "../NotifyDot/NotifyDot";
import { motion } from "framer-motion";
import LoadingWithText from "../LoadingWithText/LoadingWithText";
import { forceIconSize } from "src/helpers/icon-utils";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind, IButton } from "./Button.types";

const Button: React.FC<IButton> = ({
  type = "button",
  onClick,
  kind = "normal",
  text = null,
  icon,
  disabled = false,
  form = "",
  tooltip = "",
  active,
  className = "",
  hide = false,
  isLoading = false,
  autoFocus = false,
  theme = "light",
  notify = false,
  animate = true,
  capitalize = true,
  style,
  highlight = false,
}) => {
  const kindClasses = useMemo(() => {
    switch (kind) {
      case EButtonKind.GHOST: {
        return [
          "text-gray-500 dark:text-gray-200",
          "items-center",
          "justify-center",
          "!bg-transparent",
          "!border-transparent",
          active &&
            !highlight && ["!text-emerald-500", "dark:!text-purple-500"],
          highlight && !active && ["!text-red-500", "!dark:text-purple-500"],
        ];
      }
      case EButtonKind.DANGER: {
        return [
          "bg-red-400",
          "text-red-800",
          "items-center",
          "justify-center",
          "border-red-400",
          "hover:bg-red-300",
          "hover:text-red-800",
          "focus:bg-red-300",
          "focus:text-red-800",
          "active:bg-red-300",
          "active:text-red-800",
          "shadow-xs md:shadow-xs",
        ];
      }
      case EButtonKind.SAVE: {
        return [
          "bg-emerald-500",
          "text-emerald-50",
          "items-center",
          "justify-center",
          "border-emerald-400",
          "hover:bg-emerald-300",
          "hover:text-emerald-800",
          "focus:bg-emerald-300",
          "focus:text-emerald-800",
          "active:bg-emerald-300",
          "active:text-emerald-800",
          "shadow-xs md:shadow-xs",
        ];
      }
      case EButtonKind.EDIT: {
        return [
          "bg-yellow-400",
          "text-yellow-800",
          "items-center",
          "justify-center",
          "border-yellow-400",
          "hover:bg-yellow-300",
          "hover:text-yellow-800",
          "focus:bg-yellow-300",
          "focus:text-yellow-800",
          "active:bg-yellow-300",
          "active:text-yellow-800",
          "shadow-xs md:shadow-xs",
        ];
      }

      case EButtonKind.NORMAL:
      default: {
        return [
          "bg-white dark:bg-gray-900",
          "text-gray-500 dark:text-gray-200",
          "items-center",
          "justify-center",
          "border-gray-100 dark:border-gray-700",
          "hover:bg-gray-100 dark:hover:bg-gray-800",
          "hover:text-gray-800 dark:hover:text-gray-400",
          "focus:bg-gray-100 dark:hover:bg-gray-800",
          "focus:text-gray-800 dark:hover:text-gray-400",
          "active:bg-gray-100 dark:hover:bg-gray-800",
          "active:text-gray-800 dark:hover:text-gray-400",
          "shadow-xs md:shadow-xs dark:shadow-gray-900",
          active &&
            !highlight && ["!text-emerald-500", "dark:!text-purple-500"],
          highlight && !active && ["!text-red-500", "!dark:text-purple-500"],
        ];
      }
    }
  }, [kind, active, highlight]);

  if (hide) return null;

  return (
    <>
      <motion.button
        whileHover={{
          ...(animate && !disabled ? { scale: 1.05 } : {}),
        }}
        whileTap={{
          ...(animate && !disabled ? { scale: 0.9 } : {}),
        }}
        transition={{
          ...(animate && !disabled
            ? { type: "spring", stiffness: 400, damping: 17 }
            : {}),
        }}
        disabled={disabled || isLoading}
        type={type}
        title={tooltip || ""}
        onClick={disabled ? void 0 : onClick}
        autoFocus={autoFocus}
        className={clsx([
          "relative",
          "group",
          "inline-flex",
          "items-center",
          "gap-1",
          {
            "p-2 md:p-2": true,
            "h-[40px] md:h-[40px]": true,
            "min-w-[40px] md:min-w-[40px]": true,
            "opacity-60": disabled,
          },
          "rounded",
          "border",
          "md:border",
          "text-xs",
          ...kindClasses,
          className,
        ])}
        {...(form ? { form } : {})}
        style={style}
      >
        {isLoading ? (
          <>
            <div
              className={clsx([
                `h-[${ICON_SIZES.SM}px]`,
                `w-[${ICON_SIZES.SM}px]`,
                "flex",
                "items-center",
                "justify-center",
              ])}
            >
              <LoadingWithText size={ICON_SIZES.SM} />
              {/* <MdHub size={ICON_SIZES.SM} className="animate-spin" /> */}
            </div>
          </>
        ) : (
          <>
            {icon && (
              <span
                className={clsx([
                  {
                    // "group-hover:animate-pulse": animate && !disabled,
                  },
                ])}
              >
                {forceIconSize({ icon })}
              </span>
            )}
          </>
        )}
        {text && (
          <span
            className={clsx([
              {
                capitalize: capitalize,
              },
              "md:text-sm",
            ])}
          >
            {text}
          </span>
        )}
        {!!notify && <NotifyDot />}
      </motion.button>
    </>
  );
};

export default memo(Button);
